import { fork, takeEvery, put, call, take, race } from 'redux-saga/effects'
import { delay } from 'redux-saga'
import { get } from 'lodash-es'

import { subscribeToRealtimeUpdates } from '../../../Live/sagas.realtime'
import { showNotice } from '../../../Layout/Notices/Notices'
import { invalidateHandouts } from '../../../Handouts/blocs/useHandouts'
import { invalidatePolls } from '../../../Polls/blocs/usePolls'

function* waitRandomTime(delaySeconds, spreadSeconds) {
  // Wait random time between (delaySeconds) to (delaySeconds+spreadSeconds) seconds, just to not trigger all clients api requests at the exact same time
  // since this would force all clients to miss the cache, rather spread it out a bit
  // (Math.random() * spread) + delay
  const random = Math.random() * spreadSeconds
  // oxlint-disable-next-line typescript/restrict-plus-operands
  const factor = random + delaySeconds
  yield delay(factor * 1000)
}

function* handlePollPublish(isProducer) {
  if (isProducer) {
    return
  }

  yield call(waitRandomTime, 1, 4)
  yield call(invalidatePolls)
}

function* handlePollAnswer(isProducer, actionNames) {
  if (!isProducer) {
    return
  }

  yield put({ type: actionNames.POLL_ANSWERED })
}

function* debouncePollAnswer(isProducer, actionNames) {
  if (!isProducer) {
    return
  }

  // in new version of redux-saga there is debounce effect
  // to replace the code below when available
  while (true) {
    yield take(actionNames.POLL_ANSWERED)

    while (true) {
      const { debounced } = yield race({
        debounced: delay(1000),
        latestAction: take(actionNames.POLL_ANSWERED),
      })

      if (debounced) {
        yield call(invalidatePolls)
        break
      }
    }
  }
}

// oxlint-disable-next-line import/no-default-export
export default ({ actionNames, props }) => {
  const { defaultSidebarTab, role } = props

  const isProducer = role === 'producer'

  function* watchRealtimeUpdates() {
    // throw comments action when realtime arrives
    yield takeEvery(
      actionNames.REALTIME_DATA_UPDATE,
      function* onRealtimeDataUpdate(action) {
        const eventType = get(action, 'realtimeData.event')

        // oxlint-disable-next-line typescript/no-unnecessary-condition
        if (eventType === 'poll_publish') {
          yield fork(handlePollPublish, isProducer)
          return
        }

        // oxlint-disable-next-line typescript/no-unnecessary-condition
        if (eventType === 'poll_answer') {
          yield fork(handlePollAnswer, isProducer, actionNames)
          return
        }

        // oxlint-disable-next-line typescript/no-unnecessary-condition
        if (eventType === 'live_setting_change') {
          yield put({ type: actionNames.REFETCH_LIVE_DATA })
          return
        }

        // oxlint-disable-next-line typescript/no-unnecessary-condition
        if (eventType === 'handout_update') {
          yield call(invalidateHandouts)
        }
      }
    )
  }

  function* enableRealtime() {
    yield fork(watchRealtimeUpdates)

    try {
      yield call(subscribeToRealtimeUpdates, actionNames.REALTIME_DATA_UPDATE)
    } catch (err) {
      showNotice({
        message:
          'Could not subscribe to realtime updates. Questions and chat functionality will not be available. Refresh the page to reconnect.',
        expirationMillis: 5e3,
        isClosable: true,
      })
    }
  }

  function* init() {
    yield put({ type: actionNames.INIT, defaultSidebarTab })

    yield fork(enableRealtime)
  }

  return {
    default: [init(), debouncePollAnswer(isProducer, actionNames)],
    init,
    watchRealtimeUpdates,
  }
}
